// SpherePreviewer.tsx 102024
// CRITICAL NOTE:
// This component handles equirectangular projections and their transformations.
// All rotations (latitude, longitude, spin) must be implemented as 3D operations
// on a spherical surface, not as 2D manipulations of a rectangle.
// Refer to the G'MIC script for correct mathematical transformations and
// efficient implementation techniques.
// IMPORTANT: Color Preservation Guidelines
// 1. Use MeshBasicMaterial for simple, unlit rendering
// 2. Maintain consistent sRGB color space usage
// 3. Avoid tone mapping (use THREE.NoToneMapping)
// 4. Be cautious with custom shaders and color modifications
// 5. Check for double application of gamma correction
// 6. Use simple texture loading without extra processing
// 7. Avoid post-processing effects
// 8. Test color rendering frequently with small changes
// 9. Implement easy toggling between original and modified rendering
// 10. Consider alternative projection methods that don't require problematic geometry adjustments

import React, { useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { EquirectangularReflectionMapping, WebGLCubeRenderTarget } from 'three';
import { OrbitControls } from '@react-three/drei';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';

interface SphereProps {
  texture: string;
  tilt: number;
  offset: number;
  isRotating: boolean;
  rotation: number;
  glContext: WebGL2RenderingContext | null;
  sharedTexture: WebGLTexture | null;
  globalRotationRef?: React.MutableRefObject<number>;
  imageWidth?: number;  // Add this line
}

const Sphere: React.FC<SphereProps> = ({ 
  texture, 
  tilt, 
  offset, 
  isRotating, 
  rotation,
  globalRotationRef 
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const rotationRef = useRef<number>(0);
  const textureMap = useLoader(THREE.TextureLoader, texture);
  const { gl } = useThree();

  useEffect(() => {
    if (textureMap) {
      // Ensure it's in equirectangular format
      textureMap.mapping = THREE.EquirectangularReflectionMapping;
      textureMap.needsUpdate = true;

      // Convert to cubemap
      const cubeRenderTarget = new THREE.WebGLCubeRenderTarget(512, {
        format: THREE.RGBFormat,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
      });
      cubeRenderTarget.fromEquirectangularTexture(gl, textureMap);

      // Apply the cubemap texture to the sphere
      if (meshRef.current) {
        meshRef.current.material = new THREE.MeshBasicMaterial({
          map: cubeRenderTarget.texture,
        });
        meshRef.current.material.needsUpdate = true;
      }
    }
  }, [textureMap, gl]);

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.x = THREE.MathUtils.degToRad(tilt);
      if (isRotating) {
        rotationRef.current += 0.01; // Increased from 0.005 to get full 360°
        meshRef.current.rotation.y = rotationRef.current;
        if (globalRotationRef) {
          globalRotationRef.current = rotationRef.current;
        }
      } else {
        meshRef.current.rotation.y = rotationRef.current;
        if (globalRotationRef) {
          globalRotationRef.current = rotationRef.current;
        }
      }
    }
});

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[1, 64, 64]} />
    </mesh>
  );
};

const Scene: React.FC<SphereProps & { onFrame: () => void }> = (props) => {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 0, 2.75);
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = 50;
      camera.updateProjectionMatrix();
    }
  }, [camera]);

  useFrame(() => {
    props.onFrame();
  });

  return (
    <>
      <Sphere {...props} />
      <OrbitControls enableRotate={!props.isRotating} enableZoom={false} enablePan={false} />
    </>
  );
};

interface SpherePreviewer extends SphereProps {
  ref?: React.Ref<{ 
    recordVideo: (onComplete?: (frameCount: number) => void) => void;
    getScene: () => THREE.Scene;
    exportModel: (binary: boolean) => void;
    captureSnapshot: () => Promise<string>;  // Changed from captureGridSnapshots
  }>;
}

const SpherePreviewer = forwardRef<{
  recordVideo: (onComplete?: (frameCount: number) => void) => void;
  getScene: () => THREE.Scene;
  exportModel: (binary: boolean) => void;
  captureSnapshot: () => Promise<string>;  // Changed from captureGridSnapshots
}, SpherePreviewer>((props, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const globalRotationRef = useRef<number>(0);

  const recordVideo = (onComplete?: (frameCount: number) => void) => {
    if (canvasRef.current) {
      const startRotation = globalRotationRef.current;
      const stream = canvasRef.current.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType: 'video/webm; codecs=vp9',
        videoBitsPerSecond: 8000000
      });
      const chunks: Blob[] = [];
      let isComplete = false;
      let frameCount = 0;
      
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const exportFilename = localStorage.getItem('exportFilename') || 'sphere_rotation.webm';
        a.download = exportFilename;
        a.click();
        URL.revokeObjectURL(url);
        
        // Pass frame count back to parent
        if (onComplete) {
          onComplete(frameCount);
        }
      };
  
      mediaRecorder.start();
      
      const checkRotation = setInterval(() => {
        frameCount++;
        const rotationDelta = Math.abs(globalRotationRef.current - startRotation);
        if (rotationDelta >= 2 * Math.PI) {
          isComplete = true;
        }
        if (isComplete && mediaRecorder.state === 'recording') {
          clearInterval(checkRotation);
          mediaRecorder.stop();
        }
      }, 50);
  
      setTimeout(() => {
        clearInterval(checkRotation);
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }
      }, 10500);
    }
  };

  const exportModel = (binary: boolean) => {
    if (!sceneRef.current) {
      console.error('Scene is not available');
      return;
    }
  
    const scene = sceneRef.current;
    const exporter = new GLTFExporter();
  
    const clonedScene = scene.clone();
    const sphereMesh = clonedScene.children.find(child => child instanceof THREE.Mesh) as THREE.Mesh;
  
    if (sphereMesh && sphereMesh.material instanceof THREE.MeshBasicMaterial) {
      const newMaterial = new THREE.MeshStandardMaterial();
      newMaterial.map = sphereMesh.material.map;
      sphereMesh.material = newMaterial;
    }
  
    exporter.parse(
      clonedScene,
      (gltf) => {
        let blob: Blob;
        if (binary) {
          blob = new Blob([gltf as ArrayBuffer], { type: 'application/octet-stream' });
        } else {
          const output = JSON.stringify(gltf, null, 2);
          blob = new Blob([output], { type: 'text/plain' });
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = binary ? 'sphere.glb' : 'sphere.gltf';
        link.click();
        URL.revokeObjectURL(url);
      },
      (error) => {
        console.error('An error happened during export:', error);
      },
      { binary: binary }
    );
  };

  const captureSnapshot = async (): Promise<string> => {
    if (!canvasRef.current) {
      throw new Error('Canvas is not available');
    }
  
    // Store original rotation state
    const isRotating = props.isRotating; // Store whether it was rotating
  
    const canvas = document.createElement('canvas');
    canvas.width = canvasRef.current.width;
    canvas.height = canvasRef.current.height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
  
    await new Promise(resolve => requestAnimationFrame(resolve));
  
    // Capture the frame
    ctx.drawImage(canvasRef.current, 0, 0);
  
    // No need to stop/restart rotation - just capture current frame
    return canvas.toDataURL('image/png');
  };
  
  useImperativeHandle(ref, () => ({
    recordVideo,
    getScene: () => {
      if (!sceneRef.current) {
        throw new Error('Scene is not available');
      }
      return sceneRef.current;
    },
    exportModel,
    captureSnapshot  // Changed from captureGridSnapshots
  }));

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Canvas 
        ref={canvasRef} 
        camera={{ 
          fov: 50, 
          near: 0.1, 
          far: 1000, 
          position: [0, 0, 2.75]
        }}
        onCreated={({ scene, gl, camera }) => { 
          sceneRef.current = scene;
          gl.outputColorSpace = THREE.LinearSRGBColorSpace;
          gl.toneMapping = THREE.NoToneMapping;
          
          if (camera instanceof THREE.PerspectiveCamera) {
            camera.updateProjectionMatrix();
          }
        }}
      >
        <Scene {...props} globalRotationRef={globalRotationRef} onFrame={() => {}} />
      </Canvas>
    </div>
  );
});

export default SpherePreviewer;